<template>
	<div class="small-12 flex page propertyDetails animated fadeIn" v-if="loaded && user">
		<primary-nav :user="user"/>
		<div class="small-12 large-10 pageContentWrap">
			<div class="small-12 pageContent">
				<div class="small-12 centerColumn">
					<div class="small-12 pageNav flex vertical-align border-bottom" v-if="property">
						<property-nav :property="property"/>
						<site-nav :user="user"/>
					</div>
					<div class="small-12 large-9 details">
						<div class="small-12 large-8 columns">
							<property-images :property="property"/>
							<property-details :property="property" @openAddPurchaseMenu="openThePurchaseMenu"/>
							<property-description :property="property"/>
							<property-taxes :property="property"/>
						</div>
						<div class="small-12 large-4 columns">
							<property-balance :property="property"/>
							<projected-return :property="property"/>
							<unit-mix :property="property"/>
						</div>
						<user-footer/>
					</div>
					<div class="small-12 large-3 map">
						<property-map :property="property" v-if="property"/>
						<add-purchase :purchase="purchase" :menu_action="openPurchaseMenu" @closeAddPurchaseMenu="closeThePurchaseMenu"/>
					</div>
				</div>
			</div>
		</div>
		<new-version-modal :show_modal="!user.attributes.has_latest_version" v-show="!user.attributes.has_latest_version"/>
	</div>
	<loading-spinner :loaded="loaded" v-else/>
</template>

<script>
	
	// Import firebase
	import { firebaseAuth, usersModel, propertiesModel } from '@/firebase/firebase-config'

	// Import the components
	import UserFooter from '@/components/framework/footer/UserFooter.vue'
	import LoadingSpinner from '@/components/framework/helpers/LoadingSpinner.vue'
	import PrimaryNav from '@/components/framework/nav/PrimaryNav.vue'
	import SiteNav from '@/components/framework/nav/SiteNav.vue'
	import PropertyNav from '@/components/property/shared/PropertyNav.vue'
	import PropertyMap from '@/components/property/details/PropertyMap.vue'
	import PropertyImages from '@/components/property/details/PropertyImages.vue'
	import PropertyBalance from '@/components/property/details/PropertyBalance.vue'
	import PropertyDetails from '@/components/property/details/PropertyDetails.vue'
	import ProjectedReturn from '@/components/property/details/ProjectedReturn.vue'
	import PropertyDescription from '@/components/property/details/PropertyDescription.vue'
	import UnitMix from '@/components/property/details/UnitMix.vue'
	import PropertyTaxes from '@/components/property/details/PropertyTaxes.vue'
	import AddPurchase from '@/components/property/details/AddPurchase.vue'
	import NewVersionModal from '@/components/framework/modals/NewVersionModal.vue'

	// Import the mixins
	import { BugsnagService } from '@/mixins/services/bugsnag/BugsnagService'

	// Import the events
	import { GetAppVersion } from '@/events/app/GetAppVersion'

	export default {

		/*
		** Set the component name
		*/

		name: 'Index',

		/*
		**  Register the components
		*/

		components: {

			LoadingSpinner,
			UserFooter,
			PrimaryNav,
			SiteNav,
			PropertyNav,
			PropertyMap,
			PropertyImages,
			PropertyBalance,
			PropertyDetails,
			ProjectedReturn,
			PropertyDescription,
			UnitMix,
			PropertyTaxes,
			AddPurchase,
			NewVersionModal

		},

		/*
		**  Register the mixins
		*/

		mixins: [

			BugsnagService,
			GetAppVersion

		],

		/*
		**  Set up the data
		*/

		data: () => ({

			user: null,
			property: null,
			loaded: false,
			openPurchaseMenu: false,
			purchase: null

		}),

		/*
		**  Do this first
		*/

		created() {

			// Get the user
			this.getUser();

		},

		/*
		**  Set up the methods
		*/

		methods: {

			getUser: function() {

				// Get the user doc
				usersModel.doc(firebaseAuth.currentUser.uid).get()
				.then(doc => {

					if (!doc.exists) return;

					// Set the prop
					this.user = doc.data();

				})
				.then(() => {

					// Get the property
					if (this.user) this.getProperty();

				})
				.catch(error => {

					// Send to bugsnag
					this.reportAuthBugsnagError({
						title: 'Property Details Index - Get User Document',
						errorType: 'Firestore Database',
						section: 'Property',
						component: 'Index',
						error: error
					});

				});

			},

			getProperty: function() {

				// Get the property doc
				propertiesModel.doc(this.$route.params.id).get()
				.then(doc => {

					if (!doc.exists) return;

					// Check if owned by user
					if (this.user.id == doc.data().relationships.user) {

						// Set the prop
						this.property = doc.data();

					} else {

						// Notify the user
						this.$swal('Uh Oh!', 'Looks like you don\'t have the necessary permissions to view this property.', 'error');

						// Redirect user to properties dashboard page
						this.$router.push({ name: 'PropertiesDashboard' });

					}

				})
				.then(() => {

					// Get the purchase record
					if (this.property) this.getLatestPurchase();

				})
				.catch(error => {

					// Send to bugsnag
					this.reportAuthBugsnagError({
						title: 'Property Details Index - Get Property Document',
						errorType: 'Firestore Database',
						section: 'Property',
						component: 'Index',
						error: error
					});

				});

			},

			getLatestPurchase: function() {

				// Get the doc
				propertiesModel.doc(this.property.id).collection('purchases').where('relationships.user', '==', this.user.id).where('meta.deleted_at', '==', null).limit(1).orderBy('attributes.purchase_date', 'desc').get()
				.then(snapshot => {

					if (snapshot.empty) return;

					snapshot.forEach(doc => {

						this.purchase = doc.data();

					});

				})
				.then(() => {

					// Set the prop
					this.loaded = true;

				})
				.catch(error => {

					// Send to bugsnag
					this.reportAuthBugsnagError({
						title: 'Property Details - Get Latest Purchase',
						errorType: 'Firestore Database',
						section: 'Property',
						component: 'Index',
						error: error
					});

				});

			},

			openThePurchaseMenu: function(value) {

				if (value) {

					// Open the menu
					this.openPurchaseMenu = true;

				} else {

					// Close the menu
					this.openPurchaseMenu = false;

				}

			},

			closeThePurchaseMenu: function(value) {

				// Close the menu
				this.openPurchaseMenu = false;

			}

		}

	}

</script>